import React from 'react'
import { Tooltip } from '@mui/material'
import {
  Add as AddIcon, ArrowBack, ArrowForward, DarkMode as DarkModeIcon, DeleteOutline, EditOutlined,
  LightMode as LightModeIcon, Replay as RestartIcon, Search as SearchIcon, VisibilityOutlined as PreviewIcon,
} from '@mui/icons-material'
import { io } from 'socket.io-client'
import { PrivatePage } from '../component/PagePrivate'
import { ViewToggle } from '../component/ViewToggle'
import { NotificationV2 } from '../container/common/NotificationV2'
import { ProfileV2 } from '../container/common/ProfileV2'
import { ConfirmV2 } from '../component/ConfirmV2'
import { TelegramCampaignViewV2, TelegramDialogV2 } from '../container/marketing/telegram/TelegramDialogV2'
import { CampaignDialogV2 } from '../container/marketing/campaign/CampaignDialogV2'
import { CampaignPreviewV2 } from '../container/marketing/campaign/CampaignPreviewV2'
import { TableRef } from '../component/datatable/types'
import { FormState } from '../function/validation'
import { formatDate } from '../function/date'
import { api } from '../function/axios'
import { useDispatch, useSelector } from '../store'
import { activateCampaign, deleteCampaign } from '../store/marketing/actionCreator'
import { ICampaign, ITelegramCampaign } from '../store/marketing/reducer'
import { useAdminTheme } from '../hook/useAdminTheme'
import l from './SubscribersPageV2.module.css'
import s from './MarketingPageV2.module.css'

/*
 * Marketing in the v2 admin design. The classic page (MarketingPage.tsx) is
 * untouched; this one reads the same endpoints (/marketing-campaign,
 * /marketing-tg-campaign). Pop-ups are created and edited in CampaignDialogV2
 * (FormCampaign's fields, rules and request body, redesigned, with a live
 * preview); Telegram campaigns in TelegramDialogV2 (FormTelegram's rules and
 * request, redesigned, with a Telegram preview).
 *
 * The pop-up preview is the user's own (CampaignPreviewV2 — the new
 * dashboard's PopupCampaign), not the classic PopupPreview.
 *
 * What is new on top: whether a pop-up is live (it runs for its duration from
 * `createdTime` — the user's countdown, espy-dashboard container/PopupCampaign
 * — and "Restart" is the classic form's activate), the real pop-up type
 * (the classic table printed "Bottom-right corner" for every one), and
 * totals over all campaigns.
 *
 * The backend pages these lists 9 at most (limit < 10), so each list is read
 * page by page in full, then searched and paged here.
 */

const PER_PAGE = 10
const SERVER_PAGE = 9
type Tab = 'popups' | 'telegram'

const socketUrl: string | undefined = process.env.REACT_APP_WEBSOCKET_HOST
const socket = socketUrl ? io(socketUrl) : null

const PAGE_LABEL: Record<string, string> = {
  all: 'All pages', '/': 'Dashboard', '/search': 'Search', '/packages': 'Packages', '/developer': 'Developer',
  '/apikey': 'API key', '/support': 'Support',
}
const TYPE_LABEL: Record<string, string> = { 'bottom-right': 'Bottom-right corner', 'center-overlay': 'Center overlay' }
const FREQ_LABEL: Record<string, string> = { 'once-a-day': 'Once a day', 'every-login': 'Every login' }

async function loadAll<T>(url: string): Promise<T[]> {
  const out: T[] = []
  for (let offset = 0, guard = 0; guard < 200; guard += 1, offset += SERVER_PAGE) {
    // eslint-disable-next-line no-await-in-loop
    const { data } = await api({ method: 'GET', url, params: { offset, limit: SERVER_PAGE } })
    const list: T[] = Array.isArray(data) ? data : (data?.list || [])
    out.push(...list)
    const count = Array.isArray(data) ? null : Number(data?.count)
    if (list.length < SERVER_PAGE || (count != null && out.length >= count)) break
  }
  return out
}

/* the user's countdown: live from createdTime for finalDuration (duration as a fallback) */
function liveState(c: ICampaign): { live: boolean; left?: string } {
  const d: any = (c as any).finalDuration || c.duration
  const secs = (Number(d?.hours) || 0) * 3600 + (Number(d?.minutes) || 0) * 60
  const start = new Date(c.createdTime).getTime()
  if (!secs || Number.isNaN(start)) return { live: false }
  const left = start + secs * 1000 - Date.now()
  if (left <= 0) return { live: false }
  const h = Math.floor(left / 3600000)
  const m = Math.floor((left % 3600000) / 60000)
  const days = Math.floor(h / 24)
  return { live: true, left: days ? `${days}d ${h % 24}h left` : h ? `${h}h ${m}m left` : `${m}m left` }
}

function Pager({ page, pages, total, onPage }: { page: number; pages: number; total: number; onPage: (p: number) => void }) {
  if (pages <= 1) return null
  return (
    <div className={s.pager}>
      <span>{page * PER_PAGE + 1}–{Math.min(total, (page + 1) * PER_PAGE)} of {total}</span>
      <div className={s.pagerBtns}>
        <button type="button" className={s.act} disabled={page === 0} onClick={() => onPage(page - 1)} aria-label="Previous page"><ArrowBack sx={{ fontSize: 16 }} /></button>
        <button type="button" className={s.act} disabled={page >= pages - 1} onClick={() => onPage(page + 1)} aria-label="Next page"><ArrowForward sx={{ fontSize: 16 }} /></button>
      </div>
    </div>
  )
}

export function MarketingPageV2() {
  const dispatch = useDispatch()
  const user = useSelector((state) => state.settings)
  const { theme, toggleTheme } = useAdminTheme()

  const [tab, setTab] = React.useState<Tab>('popups')
  const [campaigns, setCampaigns] = React.useState<ICampaign[] | null>(null)
  const [telegram, setTelegram] = React.useState<ITelegramCampaign[] | null>(null)
  const [query, setQuery] = React.useState('')
  const [status, setStatus] = React.useState<'all' | 'live' | 'ended'>('all')
  const [page, setPage] = React.useState(0)

  const [createPopup, setCreatePopup] = React.useState(false)
  const [createTelegram, setCreateTelegram] = React.useState(false)
  const [edit, setEdit] = React.useState<ICampaign | null>(null)
  const [preview, setPreview] = React.useState<FormState | null>(null)
  const [tgPreview, setTgPreview] = React.useState<ITelegramCampaign | null>(null)
  const [toDelete, setToDelete] = React.useState<ICampaign | null>(null)
  const [toRestart, setToRestart] = React.useState<ICampaign | null>(null)
  // re-render each minute so "N left" and Live / Ended stay true
  const [, setTick] = React.useState(0)
  React.useEffect(() => { const id = setInterval(() => setTick((n) => n + 1), 60000); return () => clearInterval(id) }, [])

  const loadCampaigns = React.useCallback(async () => {
    try { setCampaigns(await loadAll<ICampaign>('/marketing-campaign')) } catch { setCampaigns([]) }
  }, [])
  const loadTelegram = React.useCallback(async () => {
    try { setTelegram(await loadAll<ITelegramCampaign>('/marketing-tg-campaign')) } catch { setTelegram([]) }
  }, [])
  React.useEffect(() => { void loadCampaigns(); void loadTelegram() }, [loadCampaigns, loadTelegram])

  // the bot reports sending progress over the socket, as on the classic page
  React.useEffect(() => {
    if (!socket || !user.id) return undefined
    const onNotice = (n: any) => {
      if (String(user.id) === String(n?.user_id) && n?.notification_type === 'tgcampaign_notification') void loadTelegram()
    }
    socket.on('notification', onNotice)
    return () => { socket.off('notification', onNotice) }
  }, [user.id, loadTelegram])

  // the classic forms refresh "their table" — here, the list behind it
  const campaignRef = React.useRef<TableRef>({ refresh: () => { void loadCampaigns() }, getSelectedIds: () => [] })
  const telegramRef = React.useRef<TableRef>({ refresh: () => { void loadTelegram() }, getSelectedIds: () => [] })
  campaignRef.current = { refresh: () => { void loadCampaigns() }, getSelectedIds: () => [] }
  telegramRef.current = { refresh: () => { void loadTelegram() }, getSelectedIds: () => [] }

  React.useEffect(() => { setPage(0) }, [tab, query, status])

  const q = query.trim().toLowerCase()
  const popupRows = (campaigns || []).filter((c) => {
    if (status !== 'all' && liveState(c).live !== (status === 'live')) return false
    return !q || [c.name, c.title, c.paragraph].some((v) => String(v || '').toLowerCase().includes(q))
  })
  const tgRows = (telegram || []).filter((c) => !q || [c.name, c.title, c.paragraph].some((v) => String(v || '').toLowerCase().includes(q)))
  const list: any[] = tab === 'popups' ? popupRows : tgRows
  const pages = Math.max(1, Math.ceil(list.length / PER_PAGE))
  const rows = list.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE)

  const all = campaigns || []
  const views = all.reduce((n, c) => n + (Number(c.viewCount) || 0), 0)
  const clicks = all.reduce((n, c) => n + (Number(c.actionCount) || 0), 0)
  const live = all.filter((c) => liveState(c).live).length
  const sent = (telegram || []).reduce((n, c) => n + (Number(c.results?.successfulSent) || 0), 0)
  const blocked = (telegram || []).reduce((n, c) => n + (Number(c.results?.failedSent) || 0), 0)

  const previewOf = (c: ICampaign): FormState => {
    const d: any = (c as any).finalDuration || c.duration || {}
    return { ...c, durationHour: d.hours, durationMinute: d.minutes } as unknown as FormState
  }

  const header = (
    <div className={`${l.theme} ${l.header}`} data-theme={theme}>
      <div className={l.crumb}><small>Growth</small><b>Marketing</b></div>
      <div className={l.spacer} />
      <label className={l.search}>
        <SearchIcon sx={{ fontSize: 17 }} />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={tab === 'popups' ? 'Search pop-ups' : 'Search Telegram campaigns'} />
      </label>
      <ViewToggle dark={theme === 'dark'} />
      <Tooltip title={theme === 'dark' ? 'Light theme' : 'Dark theme'}>
        <button className={l.iconBtn} onClick={toggleTheme} aria-label="Toggle theme">
          {theme === 'dark' ? <LightModeIcon sx={{ fontSize: 17 }} /> : <DarkModeIcon sx={{ fontSize: 17 }} />}
        </button>
      </Tooltip>
      <NotificationV2 />
      <ProfileV2 />
    </div>
  )

  return (
    <PrivatePage header={header}>
      <div className={`${l.theme} ${l.page}`} data-theme={theme}>
        <div className={`${l.kpis} ${s.kpis}`}>
          <div className={l.kpi}><small>Pop-up campaigns</small><b>{campaigns ? all.length : '—'}</b><span>{live} live now</span></div>
          <div className={l.kpi}><small>Views</small><b>{campaigns ? views.toLocaleString() : '—'}</b><span>all pop-ups</span></div>
          <div className={l.kpi}><small>Clicks</small><b>{campaigns ? clicks.toLocaleString() : '—'}</b><span>on the call to action</span></div>
          <div className={l.kpi}><small>Click rate</small><b>{views ? `${((clicks / views) * 100).toFixed(1)}%` : '—'}</b><span>clicks / views</span></div>
          <div className={l.kpi}>
            <small>Telegram</small><b>{telegram ? telegram.length : '—'}</b>
            <span>{sent.toLocaleString()} sent · {blocked.toLocaleString()} blocked</span>
          </div>
        </div>

        <div className={l.sec}>
          <div className={s.seg}>
            <button type="button" className={`${s.segBtn} ${tab === 'popups' ? s.segOn : ''}`} onClick={() => setTab('popups')}>
              Pop-ups <span>{all.length}</span>
            </button>
            <button type="button" className={`${s.segBtn} ${tab === 'telegram' ? s.segOn : ''}`} onClick={() => setTab('telegram')}>
              Telegram <span>{(telegram || []).length}</span>
            </button>
          </div>
          <div className={l.spacer} />
          {tab === 'popups' ? (
            <>
              <div className={s.chips}>
                {([['all', 'All'], ['live', 'Live'], ['ended', 'Ended']] as const).map(([k, label]) => (
                  <button key={k} type="button" className={`${l.chip} ${status === k ? l.chipOn : ''}`} onClick={() => setStatus(k)}>{label}</button>
                ))}
              </div>
              <button type="button" className={s.primary} onClick={() => setCreatePopup(true)}><AddIcon sx={{ fontSize: 17 }} />New pop-up</button>
            </>
          ) : (
            <button type="button" className={s.primary} onClick={() => setCreateTelegram(true)}><AddIcon sx={{ fontSize: 17 }} />New Telegram campaign</button>
          )}
        </div>

        <div className={l.card}>
          <div className={l.scroll}>
            {(tab === 'popups' ? campaigns : telegram) === null ? (
              <div className={l.empty}>Loading…</div>
            ) : !rows.length ? (
              <div className={l.empty}>{q || status !== 'all' ? 'Nothing matches the filter' : 'No campaigns yet — create the first one'}</div>
            ) : tab === 'popups' ? (
              <table className={l.table}>
                <thead>
                  <tr>
                    <th>Campaign</th>
                    <th>Status</th>
                    <th>Shown</th>
                    <th>Pages</th>
                    <th>Audience</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {(rows as ICampaign[]).map((c) => {
                    const st = liveState(c)
                    const audience: string[] = []
                    const subs = (c.subscribers || []).filter(Boolean)
                    if (subs.length && !subs.includes('all')) audience.push(subs.map((x) => String(x)).join(', '))
                    if (c.packages?.length) audience.push(`Plans: ${c.packages.join(', ')}`)
                    if (c.ltvRange) audience.push(`LTV €${c.ltvRange.from}–${c.ltvRange.to}`)
                    if (c.balanceRange) audience.push(`Balance €${c.balanceRange.from}–${c.balanceRange.to}`)
                    if (c.lastActive || c.lastActive === 0) audience.push(`Inactive ${c.lastActive}+ days`)
                    return (
                      <tr key={c.id} onClick={() => setPreview(previewOf(c))} title="Show the pop-up">
                        <td>
                          <b className={s.name}>{c.name}</b>
                          <span className={l.sub}>{c.title}</span>
                          <div className={s.stats}>
                            <span title="Views">👁 {Number(c.viewCount || 0).toLocaleString()}</span>
                            <span title="Clicks">↗ {Number(c.actionCount || 0).toLocaleString()}</span>
                            <span title="Click rate">{c.percent ?? 0}%</span>
                          </div>
                        </td>
                        <td>
                          <span className={l.status}>
                            <span className={l.dot} style={{ background: st.live ? 'var(--ok)' : 'var(--text3)' }} />
                            {st.live ? 'Live' : 'Ended'}
                          </span>
                          {st.left && <span className={l.sub}>{st.left}</span>}
                          {!st.live && c.createdTime && <span className={l.sub}>since {formatDate(c.createdTime, 'dd.MM.yyyy')}</span>}
                        </td>
                        <td>
                          <span className={s.tag}>{TYPE_LABEL[c.type] || c.type}</span>
                          <span className={l.sub}>{FREQ_LABEL[c.frequency] || c.frequency}</span>
                        </td>
                        <td>
                          <div className={s.tags}>{(c.pages || []).map((p) => <span key={p} className={s.tag}>{PAGE_LABEL[p] || p}</span>)}</div>
                        </td>
                        <td>
                          {audience.length ? (
                            <div className={s.audience}>{audience.map((a) => <span key={a}>{a}</span>)}</div>
                          ) : <span className={l.muted}>Everyone</span>}
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className={s.actions}>
                            <Tooltip title="Preview"><button type="button" className={s.act} onClick={() => setPreview(previewOf(c))} aria-label="Preview"><PreviewIcon sx={{ fontSize: 17 }} /></button></Tooltip>
                            <Tooltip title="Edit"><button type="button" className={s.act} onClick={() => setEdit(c)} aria-label="Edit"><EditOutlined sx={{ fontSize: 17 }} /></button></Tooltip>
                            <Tooltip title="Restart — runs again for its duration from now"><button type="button" className={s.act} onClick={() => setToRestart(c)} aria-label="Restart"><RestartIcon sx={{ fontSize: 17 }} /></button></Tooltip>
                            <Tooltip title="Delete"><button type="button" className={`${s.act} ${s.actDel}`} onClick={() => setToDelete(c)} aria-label="Delete"><DeleteOutline sx={{ fontSize: 17 }} /></button></Tooltip>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            ) : (
              <table className={l.table}>
                <thead>
                  <tr>
                    <th>Campaign</th>
                    <th>Link</th>
                    <th>Created</th>
                    <th>Delivery</th>
                  </tr>
                </thead>
                <tbody>
                  {(rows as ITelegramCampaign[]).map((c) => {
                    const pct = c.results ? Number(c.results.percentage) || 0 : 100
                    return (
                      <tr key={c.id} onClick={() => setTgPreview(c)} title="Show the campaign">
                        <td>
                          <div className={s.tgName}>
                            {c.image ? <img src={c.image} alt="" className={s.thumb} /> : <span className={s.thumbEmpty}>TG</span>}
                            <div>
                              <b className={s.name}>{c.name}</b>
                              <span className={l.sub}>{c.title}</span>
                            </div>
                          </div>
                        </td>
                        <td className={s.link} title={c.actionUrl}>{c.actionUrl || '—'}</td>
                        <td className={l.nowrap}>{c.createdTime ? formatDate(c.createdTime, 'dd.MM.yyyy HH:mm') : '—'}</td>
                        <td>
                          <div className={s.progress}><i style={{ width: `${Math.min(100, pct)}%`, background: pct >= 100 ? 'var(--ok)' : 'var(--primary)' }} /></div>
                          <span className={l.sub}>
                            {pct >= 100 ? 'Done' : `${pct}%`}
                            {c.results?.successfulSent != null && ` · ${Number(c.results.successfulSent).toLocaleString()} sent`}
                            {c.results?.failedSent ? ` · ${Number(c.results.failedSent).toLocaleString()} blocked` : ''}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
          <Pager page={page} pages={pages} total={list.length} onPage={setPage} />
        </div>
      </div>

      {createPopup && (
        <CampaignDialogV2 mode="create" theme={theme === 'dark' ? 'dark' : 'light'} tableRef={campaignRef} onClose={() => setCreatePopup(false)} />
      )}
      {createTelegram && (
        <TelegramDialogV2 theme={theme === 'dark' ? 'dark' : 'light'} tableRef={telegramRef} onClose={() => setCreateTelegram(false)} />
      )}
      {edit && <CampaignDialogV2 mode="edit" data={edit} theme={theme === 'dark' ? 'dark' : 'light'} tableRef={campaignRef} onClose={() => setEdit(null)} />}
      {/* the pop-up as the user's dashboard draws it — for a row and for the Telegram form's Preview */}
      {preview && (
        <CampaignPreviewV2
          adminTheme={theme === 'dark' ? 'dark' : 'light'}
          onClose={() => setPreview(null)}
          data={{
            name: preview.name, title: preview.title, paragraph: preview.paragraph, actionName: preview.actionName,
            actionUrl: preview.actionUrl, type: preview.type, showCountdown: !!preview.showCountdown,
            hours: preview.durationHour, minutes: preview.durationMinute, createdTime: preview.createdTime || null,
          }}
        />
      )}
      {tgPreview && <TelegramCampaignViewV2 data={tgPreview} theme={theme === 'dark' ? 'dark' : 'light'} onClose={() => setTgPreview(null)} />}
      {toDelete && (
        <ConfirmV2
          header={`Delete “${toDelete.name}”?`}
          onYes={() => dispatch(deleteCampaign(toDelete.id, () => { void loadCampaigns() }))}
          onClose={() => setToDelete(null)}
        />
      )}
      {toRestart && (
        <ConfirmV2
          header={`Restart “${toRestart.name}”?`}
          yesLabel="Restart"
          onYes={() => dispatch(activateCampaign(toRestart.id, () => { void loadCampaigns() }))}
          onClose={() => setToRestart(null)}
        >
          It runs again for its duration from now; users who match see it again.
        </ConfirmV2>
      )}
    </PrivatePage>
  )
}
